import { Link } from 'react-router-dom';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SectionDivider from './SectionDivider';

// Two bars, not a swapped glyph: the vertical bar rotates flat to sit on top
// of the horizontal one, so plus becomes minus as one continuous turn
// instead of a hard icon swap.
function PlusMinusIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <span className="relative block w-3 h-3">
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-[1.5px] bg-current rounded-full" />
      <motion.span
        className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-[1.5px] bg-current rounded-full"
        animate={{ rotate: isOpen ? 0 : 90 }}
        transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
      />
    </span>
  );
}

/**
 * FAQ. The original 6 Q&As (Getting started x2, Legal & protection x2,
 * When a client goes quiet x2) are verbatim from src/seo/routeContent.ts's
 * HOMEPAGE_FAQ — already legally reviewed and already shipping as FAQPage
 * schema on the live homepage. Every other Q&A added on top of those six
 * is new copy written for this page: grounded only in facts already
 * established elsewhere in the product (IT Act s5 OTP signing, the
 * hash-chained evidence ledger, MSMED s18, real billing-plan terms), but
 * NOT yet run through the same legal review pass as the original six —
 * flag that before this section ships on the real homepage.
 *
 * Layout combines two reference patterns: a category pill selector up top,
 * and a chat-bubble-style expand below (question as a bar, answer as a
 * solid color card that appears under it) — recolored to brand green
 * instead of the reference's blue, since a solid color block is exactly
 * the kind of moment worth spending the one bold color choice on.
 */

/** `guide` deep-links the answer to the long-form guide that expands on it. */
type FaqItem = { q: string; a: string; guide?: { to: string; label: string } };
type FaqCategory = { label: string; items: FaqItem[] };

const CATEGORIES: FaqCategory[] = [
  {
    label: 'Getting started',
    items: [
      {
        q: 'What is Defellix?',
        a: 'Defellix is contract and reputation infrastructure for Indian freelancers — developers, designers, video editors and anyone who works project to project. It is not a marketplace and takes no commission on your work. You create the agreement, your client signs it by OTP, and the platform keeps a tamper-evident record of everything that follows.',
      },
      {
        q: 'How do I make a freelance contract in India?',
        guide: { to: '/guides/freelance-contract-india', label: 'Read the full guide to freelance contracts' },
        a: 'Define your project scope, milestones, deadlines and payment terms, then send the contract to your client as a link. They sign with an OTP sent to their email — no account and no app download needed. Under Section 5 of the Information Technology Act, 2000, that electronic signature carries the same legal weight as a wet-ink signature.',
      },
      {
        q: 'Does my client need a Defellix account to sign?',
        a: 'No. Your client opens the contract link and signs with an OTP sent to their email — there is nothing to install and no account to create. Only you, as the freelancer sending the contract, need to be signed in.',
      },
    ],
  },
  {
    label: 'Legal & protection',
    items: [
      {
        q: 'Is a digital freelance contract legally valid in India?',
        guide: { to: '/guides/electronic-signature-legal-validity-india', label: 'Read: are e-signed contracts valid in India' },
        a: 'Yes. Section 5 of the IT Act, 2000 makes an electronic signature legally equivalent to a handwritten one, and an agreement formed electronically is enforceable under the Indian Contract Act, 1872 provided it has offer, acceptance, lawful consideration and free consent. Defellix contracts use OTP-based signing and record an integrity hash at the moment of signature.',
      },
      {
        q: 'How do I deal with scope creep as a freelancer?',
        guide: { to: '/guides/scope-creep-freelance-contract-clause', label: 'Read: contract clauses that stop scope creep' },
        a: 'Fix the scope, the deliverables, the number of included revision rounds and the deadlines in the contract before work starts, and state explicitly what is out of scope. When each milestone is timestamped on submission, later requests are visibly additional work rather than part of the original agreement.',
      },
      {
        q: 'What law governs a Defellix contract?',
        a: 'The Indian Contract Act, 1872 governs the agreement itself, and the Information Technology Act, 2000 governs the electronic signature. Your contract can still name its own governing law and jurisdiction clause on top of that, the same as a paper agreement would.',
      },
    ],
  },
  {
    label: 'When a client goes quiet',
    items: [
      {
        q: 'What should I do if my client is not paying me as a freelancer in India?',
        guide: { to: '/guides/client-not-paying-freelancer-india', label: 'Read: recovery options when a client does not pay' },
        a: 'Send a written demand notice recording the amount, the due date and a deadline to pay. If you are registered under Udyam before the contract was created, you may also refer the matter to the Micro and Small Enterprises Facilitation Council under Section 18 of the MSMED Act, 2006, where no court fee is payable. Otherwise the ordinary route is a civil suit, including a summary suit under Order XXXVII of the Code of Civil Procedure, 1908.',
      },
      {
        q: 'A client ghosted me after I finished the work. What should I do?',
        guide: { to: '/guides/client-ghosted-freelancer-india', label: 'Read: what to do when a client ghosts you' },
        a: 'Gather proof that the work was delivered and accepted before you escalate. On Defellix every milestone submission and client approval is timestamped and anchored to a public blockchain, so you can produce a dated, tamper-evident record of what was delivered and when — which is the evidence a court or council actually asks for.',
      },
      {
        q: 'Can Defellix send a legal notice to my client for me?',
        guide: { to: '/guides/legal-notice-unpaid-freelance-work-india', label: 'Read: legal notice format and sample' },
        a: 'No. Defellix is a record certifier, not a notice-issuing service — it helps you assemble a dated, tamper-evident record of what was delivered and when. The demand notice itself is sent by you or your advocate, and only once the agreed due date has actually passed.',
      },
    ],
  },
  {
    label: 'Signatures & security',
    items: [
      {
        q: 'How does OTP-based e-signing actually work?',
        a: 'Your client gets a one-time password by email, tied to that specific contract. Entering it records their acceptance along with a timestamp and an integrity hash of the document at that exact moment — the same mechanism recognised under Section 5 of the IT Act, 2000.',
      },
      {
        q: "What stops a client from claiming they never signed?",
        a: 'Every signing event is written to a hash-chained evidence ledger and anchored on a public blockchain, so the timestamp and document hash cannot be altered after the fact without breaking the chain. That tamper-evidence is exactly what a dispute over "I never agreed to this" turns on.',
      },
    ],
  },
  {
    label: 'Payments & pricing',
    items: [
      {
        q: 'Do I have to pay to create my first contract?',
        a: 'No — your first contract is free under the Freemium plan. After that you can buy single contract credits, credit packs, or an unlimited monthly plan, depending on how often you send contracts.',
      },
      {
        q: 'Does Defellix take a commission on what I earn?',
        a: 'No. Defellix charges only for creating and certifying contracts — it never takes a percentage of your project fee or the payments you receive from clients.',
      },
    ],
  },
];

export default function FAQRevamp() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const category = CATEGORIES[activeCategory];

  const selectCategory = (i: number) => {
    setActiveCategory(i);
    setOpenIndex(null);
  };

  return (
    <>
      <SectionDivider />
      <section className="relative bg-black py-20 md:py-24">
        <div className="px-6 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            {/* Not a pill — a tiny preview of the section's own chat-bubble
              language below: same rounded-2xl/rounded-bl-md question-bubble
              shape, same green "answer" dot, so the tag itself previews the
              interaction pattern instead of being a generic label. */}
            <div className="inline-flex items-center gap-2.5 rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.03] pl-2.5 pr-4 py-2 mb-6">
              <span className="w-5 h-5 rounded-full bg-[#3cb44f] text-black flex items-center justify-center text-[11px] font-bold shrink-0">
                ?
              </span>
              <span className=" text-[10px] tracking-[0.3em] text-white/60 uppercase">FAQ's</span>
            </div>
            <h2 className="font-bricolage font-extrabold text-3xl md:text-5xl text-white leading-[1.15] tracking-tight text-balance">
              Questions{' '}
              <span className="font-instrumentserif italic font-normal text-accent">you'll</span> actually ask
            </h2>
          </div>

          {/* Category pills — active state reads as "selected" via a dashed
            border and a slight tilt (spring, not a hard snap), not a fill.
            Alternating tilt direction per index so a row of pills doesn't
            all lean the same way when several exist. */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 mb-12">
            {CATEGORIES.map((cat, i) => {
              const active = i === activeCategory;
              const tilt = i % 2 === 0 ? -3 : 3;
              return (
                <motion.button
                  key={cat.label}
                  type="button"
                  onClick={() => selectCategory(i)}
                  animate={{ rotate: active ? tilt : 0, scale: active ? 1.04 : 1 }}
                  whileHover={{ scale: active ? 1.04 : 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                  className={`rounded-full px-4 py-2 text-[13px] font-semibold border cursor-pointer ${active
                    ? 'border-dashed border-[#3cb44f] text-[#3cb44f]'
                    : 'border-solid border-white/15 text-white/55 hover:text-white hover:border-white/30'
                    }`}
                >
                  {cat.label}
                </motion.button>
              );
            })}
          </div>

          {/* Chat-style Q&A: question bubble on the left, answer bubble
            offset to the right underneath it — two different "speakers",
            same as a real chat thread. */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-5"
          >
            {category.items.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <motion.div layout="position" transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} key={item.q} className="flex flex-col">
                  <motion.div
                    layout="position"
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenIndex(isOpen ? null : i);
                      }
                    }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="self-start max-w-[88%] md:max-w-[75%] flex items-center gap-3 text-left cursor-pointer outline-none"
                  >
                    <span
                      className={`rounded-2xl rounded-bl-md border px-5 py-3.5 text-[14px] md:text-[15px] font-semibold transition-colors duration-300 ${isOpen
                        ? 'border-[#3cb44f]/40 bg-[#3cb44f]/[0.06] text-[#3cb44f]'
                        : 'border-white/10 bg-white/[0.03] text-white hover:border-white/20'
                        }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-300 ${isOpen ? 'border-[#3cb44f]/40 text-[#3cb44f]' : 'border-white/15 text-white/50'
                        }`}
                    >
                      <PlusMinusIcon isOpen={isOpen} />
                    </span>
                  </motion.div>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <motion.div
                          initial={{ y: -10, opacity: 0, scale: 0.97 }}
                          animate={{ y: 0, opacity: 1, scale: 1 }}
                          exit={{ y: -10, opacity: 0, scale: 0.97 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="mt-2.5 ml-10 md:ml-16 max-w-[88%] md:max-w-[75%] self-end rounded-2xl rounded-br-md bg-[#3cb44f] px-5 py-4 text-[13.5px] leading-relaxed text-black font-medium"
                        >
                          {item.a}
                          {item.guide && (
                            <Link
                              to={item.guide.to}
                              className="mt-3 block font-bold underline underline-offset-2 decoration-black/40 hover:decoration-black"
                            >
                              {item.guide.label} →
                            </Link>
                          )}
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>
    </>
  );
}
